/**
 * StatRow — the ubiquitous label ↔ value row.
 *
 * Replaces the `display:flex; justify-content:space-between` label/value
 * pairs hand-rolled across nearly every panel (stat sheets, diagnostic
 * readouts, target frames). Value can be tinted (`valueColor`) and/or set
 * in the monospace family (`mono`) for numeric/diagnostic readouts.
 */
import React from 'react';
import { COLORS, SPACE, FONTS, FONT_SIZE } from '../theme';

export interface StatRowProps {
  label: React.ReactNode;
  value: React.ReactNode;
  /** Override the value colour (e.g. a semantic status colour). */
  valueColor?: string;
  /** Render the value in the monospace family — for numbers/diagnostics. */
  mono?: boolean;
}

const rowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: SPACE.md,
  fontSize: FONT_SIZE.md,
  lineHeight: 1.4,
};

const labelStyle: React.CSSProperties = {
  color: COLORS.textSecondary,
};

const valueBaseStyle: React.CSSProperties = {
  color: COLORS.textPrimary,
  textAlign: 'right',
};

export function StatRow({
  label,
  value,
  valueColor,
  mono = false,
}: StatRowProps): React.ReactElement {
  const valueStyle: React.CSSProperties = {
    ...valueBaseStyle,
    ...(valueColor ? { color: valueColor } : null),
    ...(mono ? { fontFamily: FONTS.mono } : null),
  };
  return (
    <div style={rowStyle}>
      <span style={labelStyle}>{label}</span>
      <span style={valueStyle}>{value}</span>
    </div>
  );
}
